import { useEffect, useState } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { CheckDouble, Cross } from '@factorialco/f0-react/icons/app'
import {
  BulkCreateProjectManagementRatesDocument,
  BulkCreateProjectManagementRatesMutation,
  JobsAndRatesDataCollectionDocument,
  ProjectManagementDefaultRateCostDocument,
  ProjectManagementDefaultRateCostQuery,
  ProjectManagementDefaultRateCostQueryVariables,
} from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import LabeledInput from 'modules/core/components/LabeledInput'
import MoneyField from 'modules/core/components/MoneyField'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import RateConfigurationCard from 'modules/project_management/components/ProfitabilityCards/RateConfigurationCard'
import SkeletonLoaderCard from 'modules/project_management/components/ProfitabilityCards/RateConfigurationCard/SkeletonLoaderCard'
import RolesAndLevelsEntitySelector from 'modules/project_management/components/tables/JobsAndRatesDataCollection/RolesAndLevelsSelector'
import {
  IJobsAndRatesFormValues,
  IRateConfiguration,
  SelectedLevelItem,
} from 'modules/project_management/components/tables/JobsAndRatesDataCollection/types'
import { getLevelsDisplayText } from 'modules/project_management/lib/formatText'
import { convertProfitabilityToCents } from 'modules/project_management/lib/profitabilityCalculations'

interface IAddJobsAndRatesSidepanel {
  showAddSidePanel: boolean
  onClose: () => void
  existingResourceJobLevelIds: string[]
  isJobCatalogTreeEnabled: boolean
}

const AddJobsAndRatesSidepanel = ({
  showAddSidePanel,
  onClose,
  existingResourceJobLevelIds,
  isJobCatalogTreeEnabled,
}: IAddJobsAndRatesSidepanel) => {
  const [confirmedLevels, setConfirmedLevels] = useState<SelectedLevelItem[]>([])
  const [rateConfigurations, setRateConfigurations] = useState<Record<string, IRateConfiguration>>(
    {}
  )
  const [cachedRateCosts, setCachedRateCosts] = useState<Record<number, number>>({})
  const {
    company: { currency: companyCurrency },
  } = useCurrentSession()

  const [bulkCreateProjectManagementRates, { loading }] = useMutation(
    BulkCreateProjectManagementRatesDocument,
    {
      extractorFn: (mutationData: BulkCreateProjectManagementRatesMutation) =>
        mutationData.projectManagementMutations.bulkCreateProjectmanagementRate,
      awaitRefetchQueries: true,
      refetchQueries: [JobsAndRatesDataCollectionDocument],
    }
  )

  const [fetchDefaultRateCosts, { data: defaultRateCostsData }] = useLazyQuery<
    ProjectManagementDefaultRateCostQuery,
    ProjectManagementDefaultRateCostQueryVariables
  >(ProjectManagementDefaultRateCostDocument)

  useEffect(() => {
    const levelIds = confirmedLevels.map(({ id }) => id)
    const uncachedIds = levelIds.filter((id) => !(id in cachedRateCosts))

    if (uncachedIds.length > 0) {
      fetchDefaultRateCosts({
        variables: {
          resourceIds: uncachedIds,
          resourceType: isJobCatalogTreeEnabled ? 'JobCatalog::TreeLevel' : 'JobCatalog::Level',
        },
      })
    }
  }, [confirmedLevels, cachedRateCosts, fetchDefaultRateCosts])

  useUnsafeEffect(() => {
    if (defaultRateCostsData?.projectManagement?.defaultRateCostsConnection?.nodes) {
      const newCosts: Record<number, number> = {}
      defaultRateCostsData.projectManagement.defaultRateCostsConnection.nodes.forEach(
        (node: { resourceId: number; chargingCostCents: number }) => {
          newCosts[node.resourceId] = node.chargingCostCents / 100
        }
      )
      setCachedRateCosts((prev) => ({ ...prev, ...newCosts }))
    }
  }, [defaultRateCostsData])

  const configurationOptions = {
    different_bill_rates: i18n.t(
      'project_management.jobs_and_rates.sidepanels.form.configuration_by.options.different_bill_rates'
    ),
    same_profitability: i18n.t(
      'project_management.jobs_and_rates.sidepanels.form.configuration_by.options.same_profitability'
    ),
    ai_recomendation: i18n.t(
      'project_management.jobs_and_rates.sidepanels.form.configuration_by.options.ai_recomendation'
    ),
  }

  const { values, setValues, handleSubmit } = useForm<IJobsAndRatesFormValues>({
    defaultValues: {
      configurationBy: 'different_bill_rates',
      profitability: 0,
      levels: [],
    },
    onSubmit: async () => {
      track(trackingEvents.projectManagement.jobsAndRates.clickOnSaveJobsAndRatesButton)

      const response = await bulkCreateProjectManagementRates({
        variables: {
          rates: confirmedLevels
            .map((level) => {
              const rateConfig = rateConfigurations[level.id]
              const billingRateCents = Math.round(parseFloat(rateConfig?.billRate || '0') * 100)
              const profitabilityPercentageCents = convertProfitabilityToCents(
                parseFloat(rateConfig?.minProfitMargin || '0')
              )

              return {
                resourceId: +level.id,
                resourceType: isJobCatalogTreeEnabled
                  ? 'JobCatalog::TreeLevel'
                  : 'JobCatalog::Level',
                currency: rateConfig?.currency || 'EUR',
                billingRateCents,
                profitabilityPercentageCents,
              }
            })
            .filter((rate) => rate.billingRateCents && rate.billingRateCents !== 0),
        },
      })

      const handled = handleResult(response, () => undefined)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_saving'),
        })
      }
      handleOnClose()

      return handled
    },
  })

  const getConfigurationDisplayText = () => {
    return configurationOptions[values.configurationBy] || values.configurationBy
  }

  useEffect(() => {
    if (confirmedLevels.length === 0) {
      setRateConfigurations({})
      return
    }

    setRateConfigurations((prev) => {
      const updated: Record<string, IRateConfiguration> = {}
      const defaultProfitMargin =
        values.configurationBy === 'same_profitability' ? values.profitability : 0

      confirmedLevels.forEach((level) => {
        const levelIdNum = level.id
        const cachedCost = cachedRateCosts[levelIdNum]
        const maxCostRate = cachedCost ? +cachedCost.toFixed(2) : 0
        const billRate = maxCostRate.toFixed(2)

        const existingConfig = prev[level.id]
        updated[level.id] = existingConfig
          ? {
              ...existingConfig,
              maxCostRate,
              billRate: existingConfig.billRate === '0.00' ? billRate : existingConfig.billRate,
            }
          : {
              billRate,
              maxCostRate,
              minProfitMargin: defaultProfitMargin.toString(),
              currency: level.salaryRanges[0]?.currency || companyCurrency || 'EUR',
            }
      })

      return updated
    })
  }, [
    confirmedLevels,
    cachedRateCosts,
    values.configurationBy,
    values.profitability,
    companyCurrency,
  ])

  const handleSelectUsers = (levels: SelectedLevelItem[]) => {
    setConfirmedLevels(levels)
  }

  const handleDeleteLevel = (levelId: string) => {
    setConfirmedLevels(confirmedLevels.filter((level) => level.id !== Number(levelId)))

    setValues({
      ...values,
      levels: values.levels.filter((level) => level.id !== Number(levelId)),
    })
  }

  const handleRateConfigurationChange = (
    levelId: string,
    billRate: string,
    minProfitMargin: string
  ) => {
    const newMinProfitMargin =
      (values.configurationBy === 'same_profitability' ? values.profitability : minProfitMargin) ||
      0

    setRateConfigurations((prev) => ({
      ...prev,
      [levelId]: {
        billRate: billRate,
        minProfitMargin: newMinProfitMargin.toString(),
        maxCostRate: prev[levelId]?.maxCostRate || 0,
        currency: prev[levelId]?.currency || 'EUR',
      },
    }))
  }

  const isAnyBillRateInvalid = Object.values(rateConfigurations).some(
    (config) => +config.billRate <= 0 || config.billRate === ''
  )

  const handleOnClose = () => {
    setConfirmedLevels([])
    setRateConfigurations({})
    setCachedRateCosts({})
    onClose()
  }

  return (
    <Sidepanel
      open={showAddSidePanel}
      onClose={handleOnClose}
      title={i18n.t('project_management.jobs_and_rates.sidepanels.add_jobs.title')}
      width="wide"
      footer={
        <Box flexDirection="row" justifyContent="flexEnd" gap="s12">
          <F0Button
            icon={Cross}
            label={i18n.t('common.cancel')}
            variant="critical"
            onClick={handleOnClose}
          />
          <F0Button
            icon={CheckDouble}
            label={i18n.t('common.save')}
            loading={loading}
            disabled={
              confirmedLevels.length === 0 ||
              (isAnyBillRateInvalid && values.configurationBy === 'different_bill_rates')
            }
            onClick={handleSubmit}
            tooltip={
              isAnyBillRateInvalid
                ? i18n.t('project_management.jobs_and_rates.sidepanels.form.invalid_bill_rates')
                : undefined
            }
          />
        </Box>
      }
    >
      <Box gap="s24">
        <PopoverMenu type="dialog">
          <PopoverMenu.Trigger>
            <LabeledInput
              isLegend
              label={i18n.t('project_management.jobs_and_rates.sidepanels.form.select_jobs.label')}
            >
              <Box
                gap="s8"
                flexDirection="row"
                justifyContent="spaceBetween"
                alignItems="center"
                border={{
                  all: {
                    width: 's2',
                    style: 'solid',
                    color: 'grey400',
                  },
                }}
                borderRadius={{
                  all: 'abs008',
                }}
                padding="s8"
                paddingX="s16"
                cursor="pointer"
              >
                <Text.Normal ellipsis>{getLevelsDisplayText(confirmedLevels.length)}</Text.Normal>
                <Icon.Small icon="caret-down" color="grey600" />
              </Box>
            </LabeledInput>
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="center" width="s400" hideWhenDetached>
            <Box alignItems="center">
              <RolesAndLevelsEntitySelector
                selectedLevels={confirmedLevels}
                setValues={(partial) => {
                  const newValues = { ...values, ...partial }
                  setValues(newValues)
                  handleSelectUsers(newValues.levels)
                }}
                existingResourceJobLevelIds={existingResourceJobLevelIds}
                isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
              />
            </Box>
          </PopoverMenu.Content>
        </PopoverMenu>

        <DayPicker
          readonly
          label={i18n.t('project_management.jobs_and_rates.sidepanels.form.efective_dates.label')}
          value={moment(new Date()).format('L')}
          onChange={() => {}}
        />

        <Box gap="s12">
          <PopoverMenu type="list">
            <PopoverMenu.Trigger>
              <LabeledInput
                isLegend
                label={i18n.t(
                  'project_management.jobs_and_rates.sidepanels.form.configuration_by.label'
                )}
              >
                <Box
                  gap="s8"
                  flexDirection="row"
                  justifyContent="spaceBetween"
                  alignItems="center"
                  border={{
                    all: {
                      width: 's2',
                      style: 'solid',
                      color: 'grey400',
                    },
                  }}
                  borderRadius={{
                    all: 'abs008',
                  }}
                  padding="s8"
                  paddingX="s16"
                  cursor="pointer"
                >
                  <Text.Normal ellipsis>{getConfigurationDisplayText()}</Text.Normal>
                  <Icon.Small icon="caret-down" color="grey600" />
                </Box>
              </LabeledInput>
            </PopoverMenu.Trigger>
            <PopoverMenu.Content side="bottom" align="center" width="s400">
              <PopoverMenu.TouchableOption
                title={configurationOptions.different_bill_rates}
                onClick={() => setValues({ ...values, configurationBy: 'different_bill_rates' })}
              />
              <PopoverMenu.TouchableOption
                title={configurationOptions.same_profitability}
                onClick={() => setValues({ ...values, configurationBy: 'same_profitability' })}
              />
              <PopoverMenu.TouchableOption
                disabled
                title={configurationOptions.ai_recomendation}
                onClick={() => setValues({ ...values, configurationBy: 'ai_recomendation' })}
              />
            </PopoverMenu.Content>
          </PopoverMenu>
          {values.configurationBy === 'same_profitability' && (
            <MoneyField
              hideLabel
              label={''}
              value={values.profitability.toString()}
              currency="%"
              onChange={(value) => setValues({ ...values, profitability: Number(value) })}
            />
          )}
        </Box>
        {confirmedLevels.length > 0 && (
          <Box gap="s16">
            {confirmedLevels.map((level) => {
              const levelIdNum = level.id
              const hasCostData = cachedRateCosts[levelIdNum] !== undefined

              if (!hasCostData) {
                return <SkeletonLoaderCard key={level.id} />
              }

              return (
                <RateConfigurationCard
                  key={level.id}
                  currency={rateConfigurations[level.id]?.currency || 'EUR'}
                  maxCostRate={rateConfigurations[level.id]?.maxCostRate || 0}
                  onChangeValue={(rateValues: { billRate: string; minProfitMargin: string }) => {
                    handleRateConfigurationChange(
                      level.id.toString(),
                      rateValues.billRate,
                      rateValues.minProfitMargin
                    )
                  }}
                  onDelete={() => handleDeleteLevel(level.id.toString())}
                  peopleCount={level.totalCount}
                  title={`${level.roleName} ${level.name}`}
                  disabledAllFields={values.configurationBy === 'same_profitability'}
                  profitMargin={
                    (values.configurationBy === 'same_profitability'
                      ? values.profitability
                      : Number(rateConfigurations[level.id]?.minProfitMargin)) || 0
                  }
                  initialBillRate={rateConfigurations[level.id]?.billRate || '0.00'}
                  sameProfitMargin={values.configurationBy === 'same_profitability'}
                />
              )
            })}
          </Box>
        )}
      </Box>
    </Sidepanel>
  )
}

export default AddJobsAndRatesSidepanel
